import * as Sentry from '@sentry/react';

const details = {
  render: ['document'],
  media: ['image', 'video', 'voice', 'refresh'],
  submission: ['response'],
  cta: ['review', 'contact', 'callback', 'referral'],
  payment: ['checkout', 'confirmation'],
} as const;
type Category = keyof typeof details;
export type SurveyOutcome = {
  [K in Category]: { category: K; detail: (typeof details)[K][number]; outcome: 'success' | 'failure' | (K extends 'cta' ? 'unavailable' : never); durationMs?: number }
}[Category];
export interface SurveyObservationScope {
  enabled: boolean;
  presentation: 'legacy' | 'feedback_2026' | 'unknown';
  organizationId?: string;
  experienceId?: string;
  experienceVersionId?: string;
  flagRunId?: string;
  flagStepId?: string;
}
const scopeKeys = ['organizationId', 'experienceId', 'experienceVersionId', 'flagRunId', 'flagStepId'] as const;
const metricNames = new Set(['cx.survey.outcome', 'cx.survey.duration']);

function boundedAttributes(attributes: Record<string, unknown>) {
  const { category, detail, outcome, presentation } = attributes;
  if (typeof category !== 'string' || !Object.prototype.hasOwnProperty.call(details, category) ||
      typeof detail !== 'string' || !(details[category as Category] as readonly string[]).includes(detail) ||
      typeof outcome !== 'string' || !(['success', 'failure'].includes(outcome) || (category === 'cta' && outcome === 'unavailable')) ||
      typeof presentation !== 'string' || !['legacy', 'feedback_2026', 'unknown'].includes(presentation)) return null;
  return { category, detail, outcome, presentation };
}
const safeIdentifier = (value: unknown): value is string => typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,128}$/.test(value);

/** Metric dimensions contain only finite enums; identifiers belong to traces, never metrics. */
export function filterSurveyMetric<T extends { name: string; attributes?: Record<string, unknown> }>(metric: T): T | null {
  const attributes = boundedAttributes(metric.attributes ?? {});
  if (!metricNames.has(metric.name) || !attributes) return null;
  // Explicit build metadata only: never permit all sentry.* attributes (replay/user IDs exist).
  const metadata = Object.fromEntries(['sentry.environment', 'sentry.release'].flatMap((key) => {
    const value = metric.attributes?.[key];
    return typeof value === 'string' && /^[A-Za-z0-9_.@+:-]{1,128}$/.test(value) ? [[key, value]] : [];
  }));
  return { ...metric, attributes: { ...attributes, ...metadata } };
}

/** Outcome spans deliberately omit URLs, survey codes, media addresses, answers, and payment identifiers. */
export function filterSurveySpan<T extends { op?: string; data?: Record<string, unknown> }>(span: T): T {
  if (span.op !== 'cx.survey.outcome') return span;
  const data = span.data ?? {};
  const identifiers = Object.fromEntries(scopeKeys.flatMap((key) => safeIdentifier(data[`cx.${key}`]) ? [[`cx.${key}`, data[`cx.${key}`]]] : []));
  return { ...span, data: { ...boundedAttributes(data), ...identifiers } };
}

/** Best-effort observation must never turn a successful customer action into a failure. */
export function recordSurveyOutcome(scope: SurveyObservationScope, event: SurveyOutcome): void {
  if (!scope.enabled) return;
  const attributes = boundedAttributes({ ...event, presentation: scope.presentation });
  if (!attributes) return;
  const category = event.category;
  const identifiers = Object.fromEntries(scopeKeys.flatMap((key) => {
    const value = scope[key];
    return safeIdentifier(value) ? [[`cx.${key}`, value]] : [];
  }));
  try {
    // Isolate propagation too: merely clearing the active span can retain a private pageload name in DSC.
    Sentry.startNewTrace(() => Sentry.startSpan({ name: `cx.survey.${category}.${event.outcome}`, op: 'cx.survey.outcome', attributes: { ...attributes, ...identifiers } }, () => {
      Sentry.metrics.count('cx.survey.outcome', 1, { attributes });
      if (typeof event.durationMs === 'number' && Number.isFinite(event.durationMs) && event.durationMs >= 0)
        Sentry.metrics.distribution('cx.survey.duration', Math.min(event.durationMs, 600_000), { unit: 'millisecond', attributes });
    }));
  } catch {
    // Telemetry availability is not part of survey execution or payment acknowledgement.
  }
}

/** New root markers omit inherited request/user data; other existing transactions keep their policy. */
export function filterSurveyTransaction<T extends Sentry.Event>(event: T): T {
  if (event.contexts?.trace?.op !== 'cx.survey.outcome') return event;
  return {
    ...event,
    request: undefined,
    user: undefined,
    breadcrumbs: undefined,
    extra: undefined,
    tags: undefined,
    contexts: { trace: event.contexts.trace },
    spans: [],
  };
}
